import { easing } from '@stellar-globe/stellar-globe'
import { ChangeEvent, useMemo, useState } from 'react'
import { AppDialog } from '../../AppDialog'
import { useAppContext } from '../../context'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { bookmarksSlice } from './bookmarksSlice'
import {
  Bookmark,
  BookmarkEntry,
  FolderPath,
  decodeFolderPath,
  encodeFolderPath,
  folderOptions,
  getFolder,
  appendBookmarkFolder,
  renameBookmarkEntry,
  removeBookmarkEntry,
  reorderBookmarkEntry,
  moveBookmarkEntry,
  newBookmark,
  tryGetFolder,
} from './model'
import { useGotoBookmark } from './navigation'
import { updateBookmarkState, useBookmarkState } from './storage'

export function BookmarkDialogs() {
  return (
    <>
      <AddBookmarkDialog />
      <EditBookmarksDialog />
      <WalkSettingsDialog />
    </>
  )
}

function AddBookmarkDialog() {
  const visible = useAppSelector(state => state.bookmarks.addDialogVisible)
  const dispatch = useAppDispatch()
  const close = () => dispatch(bookmarksSlice.actions.addDialogToggled({ open: false }))

  return (
    <AppDialog
      title='Add Bookmark'
      visible={visible}
      onCloseButtonClick={close}
      rememberPosition
    >
      {visible && <AddBookmarkForm onAdded={close} />}
    </AppDialog>
  )
}

function AddBookmarkForm({ onAdded }: { onAdded: () => void }) {
  const bookmarkState = useBookmarkState()
  const { globeHandle } = useAppContext()
  const [folderPath, setFolderPath] = useState<FolderPath>([])
  const [name, setName] = useState(() => {
    const globe = globeHandle.current?.()
    if (!globe) {
      return ''
    }
    const center = globe.camera.center()
    return `${center.a.deg.toFixed(6)} ${center.d.deg >= 0 ? '+' : ''}${center.d.deg.toFixed(6)}`
  })

  const add = () => {
    const trimmedName = name.trim()
    const globe = globeHandle.current?.()
    if (!trimmedName || !globe) {
      return
    }
    const center = globe.camera.center()
    const bookmark = newBookmark(trimmedName, {
      a: center.a.rad,
      d: center.d.rad,
      fovy: globe.camera.fovy,
      roll: globe.camera.roll,
    })
    updateBookmarkState(state => {
      getFolder(state.root, folderPath).children.push(bookmark)
    })
    onAdded()
  }

  const addFolder = () => {
    const selected = folderOptions(bookmarkState.root).find(
      option => encodeFolderPath(option.path) === encodeFolderPath(folderPath),
    )
    const folderName = prompt(`Folder name ?\n> ${selected?.label ?? '/'}`)
    if (!folderName) {
      return
    }
    let childIndex = 0
    updateBookmarkState(state => {
      childIndex = appendBookmarkFolder(state.root, folderPath, folderName).at(-1)!
    })
    setFolderPath([...folderPath, childIndex])
  }

  return (
    <>
      <table>
        <tbody>
          <tr>
            <th>Folder</th>
            <td>
              <select
                value={encodeFolderPath(folderPath)}
                onChange={event => setFolderPath(decodeFolderPath(event.currentTarget.value))}
              >
                {folderOptions(bookmarkState.root).map(option => (
                  <option key={encodeFolderPath(option.path)} value={encodeFolderPath(option.path)}>
                    {option.label}
                  </option>
                ))}
              </select>
              {' '}
              <button onClick={addFolder}>New</button>
            </td>
          </tr>
          <tr>
            <th>Name</th>
            <td>
              <form onSubmit={event => {
                event.preventDefault()
                add()
              }}>
                <input
                  autoFocus
                  size={40}
                  value={name}
                  onChange={event => setName(event.currentTarget.value)}
                />
              </form>
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5em' }}>
        <button disabled={!name.trim()} onClick={add}>Add</button>
      </div>
    </>
  )
}

function EditBookmarksDialog() {
  const visible = useAppSelector(state => state.bookmarks.editDialogVisible)
  const dispatch = useAppDispatch()
  const bookmarkState = useBookmarkState()
  const gotoBookmark = useGotoBookmark()
  const [folderPath, setFolderPath] = useState<FolderPath>([])
  const selectedFolder = tryGetFolder(bookmarkState.root, folderPath)
  const effectiveFolderPath = selectedFolder ? folderPath : []
  const folder = selectedFolder ?? bookmarkState.root

  const close = () => dispatch(bookmarksSlice.actions.editDialogToggled({ open: false }))

  const addFolder = () => {
    const selected = folderOptions(bookmarkState.root).find(
      option => encodeFolderPath(option.path) === encodeFolderPath(effectiveFolderPath),
    )
    const folderName = prompt(`Folder name ?\n> ${selected?.label ?? '/'}`)
    if (!folderName) {
      return
    }
    updateBookmarkState(state => {
      appendBookmarkFolder(state.root, effectiveFolderPath, folderName)
    })
  }

  const rename = (index: number, name: string) => {
    updateBookmarkState(state => {
      renameBookmarkEntry(state.root, effectiveFolderPath, index, name)
    })
  }

  const remove = (index: number, entry: BookmarkEntry) => {
    if (!confirm(`Remove "${entry.name}" from your bookmarks?`)) {
      return
    }
    updateBookmarkState(state => {
      removeBookmarkEntry(state.root, effectiveFolderPath, index)
    })
  }

  const reorder = (index: number, delta: -1 | 1) => {
    updateBookmarkState(state => {
      reorderBookmarkEntry(state.root, effectiveFolderPath, index, delta)
    })
  }

  const move = (index: number, destinationPath: FolderPath) => {
    if (encodeFolderPath(destinationPath) === encodeFolderPath(effectiveFolderPath)) {
      return
    }
    updateBookmarkState(state => {
      moveBookmarkEntry(state.root, effectiveFolderPath, index, destinationPath)
    })
  }

  return (
    <AppDialog
      title='Edit Bookmarks'
      visible={visible}
      onCloseButtonClick={close}
      rememberPosition
      resizable
    >
      <div style={{ display: 'flex', gap: '0.5em', marginBottom: '0.5em' }}>
        <select
          value={encodeFolderPath(effectiveFolderPath)}
          onChange={event => setFolderPath(decodeFolderPath(event.currentTarget.value))}
        >
          {folderOptions(bookmarkState.root).map(option => (
            <option key={encodeFolderPath(option.path)} value={encodeFolderPath(option.path)}>
              {option.label}
            </option>
          ))}
        </select>
        <button onClick={addFolder}>New Folder</button>
      </div>
      <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
        <table>
          <tbody>
            {folder.children.map((entry, index) => {
              const entryPath = [...effectiveFolderPath, index]
              const destinations = folderOptions(
                bookmarkState.root,
                entry.isFolder ? entryPath : undefined,
              )
              return (
                <tr key={index}>
                  <td>
                    <button
                      title='Move up'
                      disabled={index === 0}
                      onClick={() => reorder(index, -1)}
                    >↑</button>
                    <button
                      title='Move down'
                      disabled={index === folder.children.length - 1}
                      onClick={() => reorder(index, 1)}
                    >↓</button>
                  </td>
                  <td>
                    <input
                      size={32}
                      value={entry.name}
                      style={entry.isFolder ? { fontWeight: 'bold' } : undefined}
                      onChange={event => rename(index, event.currentTarget.value)}
                    />
                  </td>
                  <td>
                    {entry.isFolder
                      ? <button onClick={() => setFolderPath(entryPath)}>{entry.children.length} →</button>
                      : <button onClick={() => gotoBookmark(entry as Bookmark, 400)}>→</button>}
                  </td>
                  <td>
                    <select
                      value={encodeFolderPath(effectiveFolderPath)}
                      onChange={event => move(index, decodeFolderPath(event.currentTarget.value))}
                    >
                      {destinations.map(option => (
                        <option key={encodeFolderPath(option.path)} value={encodeFolderPath(option.path)}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button title='Delete' onClick={() => remove(index, entry)}>×</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </AppDialog>
  )
}

function WalkSettingsDialog() {
  const visible = useAppSelector(state => state.bookmarks.walkSettingsVisible)
  const dispatch = useAppDispatch()
  const bookmarkState = useBookmarkState()
  const walk = bookmarkState.config.walk
  const easingNames = useMemo(
    () => Object.entries(easing)
      .filter(([, value]) => typeof value === 'function')
      .map(([name]) => name)
      .sort(),
    [],
  )

  const close = () => dispatch(bookmarksSlice.actions.walkSettingsToggled({ open: false }))
  const update = (partial: Partial<typeof walk>) => {
    updateBookmarkState(state => {
      Object.assign(state.config.walk, partial)
    })
  }

  const updateNumber = (
    key: 'duration' | 'wait',
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    update({ [key]: Number(event.currentTarget.value) })
  }

  return (
    <AppDialog
      title='Walk Setting'
      visible={visible}
      onCloseButtonClick={close}
      rememberPosition
    >
      <dl>
        <dt>Duration: {walk.duration / 1000}s</dt>
        <dd>
          <input
            type='range'
            min={0}
            max={10000}
            step={1}
            value={walk.duration}
            onChange={event => updateNumber('duration', event)}
          />
        </dd>
        <dt>Wait: {walk.wait / 1000}s</dt>
        <dd>
          <input
            type='range'
            min={0}
            max={10000}
            step={1}
            value={walk.wait}
            onChange={event => updateNumber('wait', event)}
          />
        </dd>
      </dl>
      <div>
        <label>
          Easing:{' '}
          <select value={walk.easing} onChange={event => update({ easing: event.currentTarget.value })}>
            {easingNames.map(name => <option key={name}>{name}</option>)}
          </select>
        </label>
      </div>
      <div>
        <label>
          <input
            type='checkbox'
            checked={walk.keepFovy}
            onChange={event => update({ keepFovy: event.currentTarget.checked })}
          />
          Keep FOV
        </label>
      </div>
      <div>
        <label>
          <input
            type='checkbox'
            checked={walk.loop}
            onChange={event => update({ loop: event.currentTarget.checked })}
          />
          Loop
        </label>
      </div>
    </AppDialog>
  )
}
